import { useEffect } from 'react'
import { useAppStore } from '@/store/appStore'
import apiClient from '@/services/apiClient'
import { idAuthApi } from '@/api/idAuthApi'
import { useTelegramWebApp } from '@/types/telegram'
import type { User } from '@/types/user'
import { AUTH } from '@/config/constants'
import { appConfig } from '@/config/app'
import { sanitizeIdentifier, sanitizeStartParam } from '@/utils/sanitize'
import { isBrowserAppRuntime } from '@/utils/runtime'

const applySession = (
  response: { token: string; user?: User },
  setUser: (user: User | null) => void,
  setToken: (token: string) => void,
  setIsAuthenticated: (value: boolean) => void
): void => {
  if (response.user) {
    setUser(response.user)
  }
  apiClient.setToken(response.token)
  setToken(response.token)
  setIsAuthenticated(true)
}

export const useAuth = () => {
  const { setUser, setIsAuthenticated, token, setToken, setIsLoading } = useAppStore()
  const webApp = useTelegramWebApp()

  useEffect(() => {
    const authenticate = async () => {
      setIsLoading(true)

      try {
        if (webApp?.initData) {
          let startParam = webApp.initDataUnsafe?.start_param
          if (!startParam) {
            try {
              const hashParams = new URLSearchParams(window.location.hash.slice(1))
              startParam = hashParams.get('tgWebAppStartParam') || undefined
            } catch {
              /* ignore */
            }
          }
          const safeStartParam = sanitizeStartParam(startParam)
          const response = await apiClient.login(webApp.initData, safeStartParam || undefined)
          applySession(
            { token: response.token, user: response.user as User | undefined },
            setUser,
            setToken,
            setIsAuthenticated
          )

          const pendingPaymentId = sanitizeIdentifier(localStorage.getItem(appConfig.storageKeys.pendingPaymentId))
          const pendingPaymentTs = localStorage.getItem(appConfig.storageKeys.pendingPaymentTimestamp)
          const isRecent =
            pendingPaymentTs && Date.now() - parseInt(pendingPaymentTs, 10) < AUTH.pendingPaymentTtlMs

          if (pendingPaymentId && isRecent) {
            setTimeout(() => {
              void (async () => {
                try {
                  const paymentStatus = await apiClient.syncPaymentStatus(pendingPaymentId)
                  if (paymentStatus.payment && paymentStatus.payment.status === 'completed') {
                    void apiClient.getProfile().then((user) => setUser(user))
                    localStorage.removeItem(appConfig.storageKeys.pendingPaymentId)
                    localStorage.removeItem(appConfig.storageKeys.pendingPaymentTimestamp)
                    localStorage.removeItem(appConfig.storageKeys.pendingPaymentMarker)
                  }
                } catch (err) {
                  console.error('[useAuth] Error checking payment:', err)
                }
              })()
            }, AUTH.pendingPaymentCheckDelayMs)
          }
          return
        }

        if (isBrowserAppRuntime()) {
          const bootstrapped = await idAuthApi.bootstrapAppSession()
          if (bootstrapped) {
            applySession(bootstrapped, setUser, setToken, setIsAuthenticated)
            return
          }
        }

        if (token) {
          apiClient.setToken(token)
          try {
            const profile = await apiClient.getProfile()
            if (profile) {
              setUser(profile)
            }
            setIsAuthenticated(true)
          } catch (e) {
            console.warn('Failed to fetch profile with token, clearing auth', e)
            setIsAuthenticated(false)
            apiClient.setToken('')
            setToken('')
            setUser(null)
          }
        }
      } catch (error) {
        console.error('Auto-login error:', error)
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
      }
    }

    void authenticate()
  }, [token, webApp, setIsAuthenticated, setIsLoading, setToken, setUser])
}
